import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, EyeOff, User, Phone, Mail, Lock, ArrowRight, CheckCircle2, Globe, Zap, ShieldCheck, Store } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Swal from 'sweetalert2';
import UniversalNavbar from './UniversalNavbar';
import VFooter from './VFooter';
import API_BASE_URL from '../config/api';
import { PrimaryButton } from '../atelier';
import { devLog } from '../utils/devLog';

const RegistrationForm = () => {
  const [formData, setFormData] = useState({
    username: '',
    contact_number: '',
    email: '',
    password: '',
    confirm_password: '',
    termsAccepted: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Sanitize brand username as the user types: lowercase, allow a-z, 0-9 and dashes only.
  const sanitizeSlug = (v) =>
    v.toLowerCase().replace(/[^a-z0-9-]/g, '').replace(/-{2,}/g, '-').slice(0, 30);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === 'checkbox' ? checked : name === 'username' ? sanitizeSlug(value) : value;
    setFormData((prev) => ({ ...prev, [name]: nextValue }));
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errs = {};
    const { username, email, contact_number, password, confirm_password, termsAccepted } = formData;

    if (!username || username.length < 3) {
      errs.username = 'Brand username must be at least 3 characters (letters, numbers, dashes).';
    } else if (!/^[a-z0-9][a-z0-9-]{1,28}[a-z0-9]$/.test(username)) {
      errs.username = 'Use lowercase letters, numbers, and dashes only. No spaces or special characters.';
    }

    if (!email) {
      errs.email = 'Email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errs.email = 'Please enter a valid email address.';
    }

    if (!contact_number) {
      errs.contact_number = 'Contact number is required.';
    } else if (!/^\d{10}$/.test(contact_number)) {
      errs.contact_number = 'Phone number must be exactly 10 digits.';
    }

    if (!password || password.length < 8) {
      errs.password = 'Password must be at least 8 characters.';
    }

    if (password !== confirm_password) {
      errs.confirm_password = 'Passwords do not match.';
    }

    if (!termsAccepted) {
      errs.termsAccepted = 'Please accept the terms and conditions.';
    }

    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (!validateForm()) {
      const firstErrorField = Object.keys(fieldErrors)[0] || 'username';
      const el = document.getElementById(firstErrorField);
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setErrorMessage('Please fix the highlighted fields.');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/register/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      if (data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Your store is live!',
          html: `
            <div class="text-center">
              <p class="text-lg mb-3">${data.message}</p>
              <div class="p-4 bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg border-2 border-orange-200 mb-3">
                <p class="text-sm text-gray-600 mb-1">Your store is now live at:</p>
                <p class="text-xl font-bold text-orange-600">
                  https://${formData.username}.vasstr.com
                </p>
              </div>
              <p class="text-sm text-gray-500">Your 14-day free trial is already running — log in and start adding your products.</p>
            </div>
          `,
          confirmButtonText: 'Go to Login',
          confirmButtonColor: '#d25614',
          width: '32em',
        }).then(() => {
          window.location.href = '/login';
        });
      } else {
        setErrorMessage(data.error);
      }
    } catch (error) {
      devLog.error('Error:', error);
      setErrorMessage('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const benefits = [
    { Icon: Globe, t: 'Your own branded URL', d: 'yourname.vasstr.com — no marketplace listing' },
    { Icon: Zap, t: 'Live in under 30 minutes', d: 'No code, no developer needed' },
    { Icon: Store, t: 'Keep 97% of every sale', d: 'No marketplace commission, ever' },
    { Icon: ShieldCheck, t: 'Free for 14 days', d: 'No credit card to start' },
  ];

  // Shared field styling — Atelier language.
  const fieldWrap = (hasError) =>
    `flex items-center gap-3 rounded-xl border bg-white px-4 h-[52px] transition-colors focus-within:border-primary ${
      hasError ? 'border-red-300' : 'border-line'
    }`;
  const inputCls = 'w-full bg-transparent text-[15px] text-ink outline-none placeholder:text-gray-400';
  const labelCls = 'mb-1.5 block text-[13px] font-bold text-ink';
  const errCls = 'mt-1.5 text-[12.5px] font-semibold text-red-600';

  const brandDisplay = formData.username ? formData.username.replace(/-/g, ' ') : 'Your Brand';

  return (
    <div className="min-h-screen overflow-x-hidden bg-bone text-ink">
      <UniversalNavbar />

      <div className="mx-auto max-w-7xl px-4 pt-24 pb-16 sm:px-6 sm:pt-28 lg:px-8">
        <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-2 lg:gap-14">
          {/* ───────────── Left: the form ───────────── */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-primary">
              Begin — free for 14 days
            </p>
            <h1 className="mt-3 font-fell text-4xl font-semibold leading-[1.05] text-ink sm:text-5xl">
              Open your studio.
            </h1>
            <p className="mt-4 max-w-md text-[15.5px] text-muted">
              Your own branded store at{' '}
              <span className="font-semibold text-ink">yourname.vasstr.com</span> — products,
              payments, orders and customers, all under your name.
            </p>

            <div className="mt-8 rounded-2xl border border-line bg-white p-5 shadow-[0_20px_44px_-36px_rgba(22,19,15,0.4)] sm:p-8">
              {errorMessage && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[13.5px] font-semibold text-red-700"
                  role="alert"
                >
                  {errorMessage}
                </motion.div>
              )}

              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Section 1 — Brand details */}
                <div>
                  <div className="mb-5 flex items-center gap-3">
                    <span className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-full border border-line bg-bone font-fell text-[16px] font-semibold text-ink">
                      1
                    </span>
                    <div>
                      <h3 className="font-fell text-[19px] font-semibold text-ink">Brand details</h3>
                      <p className="text-[12.5px] text-muted">Create your unique store identity</p>
                    </div>
                  </div>

                  <div className="space-y-5">
                    {/* Username */}
                    <div>
                      <label htmlFor="username" className={labelCls}>Choose your brand username</label>
                      <div className={fieldWrap(fieldErrors.username)}>
                        <User size={18} className="flex-shrink-0 text-muted" />
                        <input
                          type="text"
                          name="username"
                          id="username"
                          placeholder="e.g. mybrand, fashionstore"
                          value={formData.username}
                          onChange={handleChange}
                          autoComplete="off"
                          autoCorrect="off"
                          autoCapitalize="off"
                          spellCheck="false"
                          maxLength={30}
                          required
                          className={inputCls}
                        />
                      </div>
                      {fieldErrors.username && <p className={errCls}>{fieldErrors.username}</p>}
                      <AnimatePresence>
                        {formData.username && !fieldErrors.username && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.25 }}
                            className="mt-3 overflow-hidden"
                          >
                            <div className="flex items-center gap-2 rounded-xl bg-gray-100 px-4 py-3">
                              <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-primary" />
                              <span className="text-[13px] font-semibold text-ink">
                                Your store will be live at{' '}
                                <span className="break-all font-bold text-primary">
                                  https://{formData.username}.vasstr.com
                                </span>
                              </span>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    {/* Phone + Email */}
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                      <div>
                        <label htmlFor="contact_number" className={labelCls}>Contact number</label>
                        <div className={fieldWrap(fieldErrors.contact_number)}>
                          <Phone size={18} className="flex-shrink-0 text-muted" />
                          <input
                            type="tel"
                            name="contact_number"
                            id="contact_number"
                            placeholder="10-digit mobile"
                            value={formData.contact_number}
                            onChange={handleChange}
                            autoComplete="tel"
                            inputMode="numeric"
                            maxLength={10}
                            required
                            className={inputCls}
                          />
                        </div>
                        {fieldErrors.contact_number && <p className={errCls}>{fieldErrors.contact_number}</p>}
                      </div>
                      <div>
                        <label htmlFor="email" className={labelCls}>Email address</label>
                        <div className={fieldWrap(fieldErrors.email)}>
                          <Mail size={18} className="flex-shrink-0 text-muted" />
                          <input
                            type="email"
                            name="email"
                            id="email"
                            placeholder="you@email.com"
                            value={formData.email}
                            onChange={handleChange}
                            autoComplete="email"
                            required
                            className={inputCls}
                          />
                        </div>
                        {fieldErrors.email && <p className={errCls}>{fieldErrors.email}</p>}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 2 — Security */}
                <div>
                  <div className="mb-5 flex items-center gap-3">
                    <span className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-full border border-line bg-bone font-fell text-[16px] font-semibold text-ink">
                      2
                    </span>
                    <div>
                      <h3 className="font-fell text-[19px] font-semibold text-ink">Security</h3>
                      <p className="text-[12.5px] text-muted">Secure your account</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="password" className={labelCls}>Create password</label>
                      <div className={fieldWrap(fieldErrors.password)}>
                        <Lock size={18} className="flex-shrink-0 text-muted" />
                        <input
                          type={showPassword ? 'text' : 'password'}
                          name="password"
                          id="password"
                          placeholder="Min 8 characters"
                          value={formData.password}
                          onChange={handleChange}
                          autoComplete="new-password"
                          required
                          className={inputCls}
                        />
                        <button type="button" onClick={() => setShowPassword((v) => !v)} className="text-gray-400 transition-colors hover:text-gray-600" aria-label="Toggle password">
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      {fieldErrors.password && <p className={errCls}>{fieldErrors.password}</p>}
                    </div>
                    <div>
                      <label htmlFor="confirm_password" className={labelCls}>Confirm password</label>
                      <div className={fieldWrap(fieldErrors.confirm_password)}>
                        <Lock size={18} className="flex-shrink-0 text-muted" />
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          name="confirm_password"
                          id="confirm_password"
                          placeholder="Re-enter password"
                          value={formData.confirm_password}
                          onChange={handleChange}
                          autoComplete="new-password"
                          required
                          className={inputCls}
                        />
                        <button type="button" onClick={() => setShowConfirmPassword((v) => !v)} className="text-gray-400 transition-colors hover:text-gray-600" aria-label="Toggle password">
                          {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      {fieldErrors.confirm_password && <p className={errCls}>{fieldErrors.confirm_password}</p>}
                    </div>
                  </div>
                </div>

                {/* Terms */}
                <div>
                  <label
                    htmlFor="termsAccepted"
                    className={`flex cursor-pointer items-start gap-3 rounded-xl border bg-white px-4 py-3.5 ${
                      fieldErrors.termsAccepted ? 'border-red-300' : 'border-line'
                    }`}
                  >
                    <input
                      type="checkbox"
                      id="termsAccepted"
                      name="termsAccepted"
                      checked={formData.termsAccepted}
                      onChange={handleChange}
                      required
                      className="mt-0.5 h-5 w-5 flex-shrink-0 rounded accent-[#D45614]"
                    />
                    <span className="text-[13.5px] font-semibold text-ink">
                      I agree to the{' '}
                      <a href="/TermsNConditions" className="font-bold text-primary underline-offset-2 hover:underline">terms and conditions</a>.
                      <span className="mt-0.5 block text-[12px] font-medium text-muted">By signing up you create your own independent store website.</span>
                    </span>
                  </label>
                  {fieldErrors.termsAccepted && <p className={errCls}>{fieldErrors.termsAccepted}</p>}
                </div>

                {/* Submit — THE orange on this screen */}
                <PrimaryButton type="submit" disabled={isLoading} className="w-full py-4 text-[15.5px]">
                  {isLoading ? (
                    <>
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                      Opening your studio…
                    </>
                  ) : (
                    <>
                      Open my studio free <ArrowRight className="h-5 w-5" />
                    </>
                  )}
                </PrimaryButton>

                <div className="flex items-center gap-3">
                  <span className="h-px flex-1 bg-line" />
                  <span className="text-[12.5px] font-semibold text-muted">Already have an account?</span>
                  <span className="h-px flex-1 bg-line" />
                </div>

                <Link
                  to="/login"
                  className="flex w-full items-center justify-center gap-2 rounded-full border border-line bg-white px-6 py-3.5 text-[15px] font-semibold text-ink transition-colors hover:border-gray-300"
                >
                  Sign in to your studio
                </Link>
              </form>
            </div>

            <p className="mt-6 text-center text-[13px] text-muted lg:text-left">
              Need help?{' '}
              <a href="mailto:team@vasstr.com" className="font-semibold text-primary transition-opacity hover:opacity-70">team@vasstr.com</a>{' '}
              · +91 97043 87878
            </p>
          </motion.div>

          {/* ───────────── Right: live store preview (desktop only) ───────────── */}
          <motion.div
            className="hidden lg:flex lg:flex-col lg:gap-8 lg:sticky lg:top-28"
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* Browser-chrome mockup */}
            <div className="overflow-hidden rounded-2xl border border-line bg-white shadow-[0_40px_80px_-48px_rgba(22,19,15,0.5)]">
              {/* Chrome bar */}
              <div className="flex items-center gap-3 border-b border-line bg-gray-50 px-4 py-3">
                <span className="flex flex-shrink-0 gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full bg-gray-300" />
                  <span className="h-2.5 w-2.5 rounded-full bg-gray-200" />
                  <span className="h-2.5 w-2.5 rounded-full bg-gray-200" />
                </span>
                <span className="mx-auto flex min-w-0 max-w-[70%] items-center gap-2 rounded-full border border-line bg-white px-4 py-1.5">
                  <Lock size={11} className="flex-shrink-0 text-muted" />
                  <span className="truncate text-[12.5px]">
                    <span className="font-semibold text-ink">{formData.username || 'yourname'}</span>
                    <span className="text-muted">.vasstr.com</span>
                  </span>
                </span>
                <span className="w-10 flex-shrink-0" aria-hidden />
              </div>

              {/* Live store canvas */}
              <div className="relative">
                <img
                  src={`${API_BASE_URL}/static/template_snapshots/t01.jpg`}
                  alt="Your store preview"
                  className="h-[440px] w-full object-cover object-top"
                />
                <div className="absolute inset-0 flex items-end bg-gradient-to-t from-ink/75 via-ink/10 to-transparent">
                  <div className="w-full px-8 pb-8">
                    <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-white/70">
                      Est. {new Date().getFullYear()} — your label
                    </p>
                    <p className="mt-1.5 break-words font-fell text-4xl font-semibold capitalize leading-[1.08] text-white">
                      {brandDisplay}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <p className="-mt-3 text-center text-[12.5px] text-muted">
              Live preview — this becomes your store the moment you sign up.
            </p>

            {/* Reassurance */}
            <div className="grid grid-cols-2 gap-3">
              {benefits.map((b) => (
                <div key={b.t} className="flex items-start gap-3 rounded-2xl border border-line bg-white p-4">
                  <span className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-xl bg-gray-100 text-primary">
                    <b.Icon className="h-[18px] w-[18px]" />
                  </span>
                  <span>
                    <span className="block text-[13px] font-bold text-ink">{b.t}</span>
                    <span className="block text-[12px] text-muted">{b.d}</span>
                  </span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      <VFooter />
    </div>
  );
};

export default RegistrationForm;
